import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Navigate, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import {
  PendingInvitationsProvider,
  usePendingInvitationsContext,
} from "../../contexts/PendingInvitationsContext";

const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(!isAuthenticated);

  useEffect(() => {
    // If already authenticated, no check needed
    if (isAuthenticated) {
      setChecking(false);
      return;
    }
    // There may be a valid refresh token even though isAuthenticated is false
    // (e.g. access token expired but refresh token is still valid).
    if (localStorage.getItem("refreshToken")) {
      refresh().then((ok) => {
        if (!ok) {
          const redirect = encodeURIComponent(location.pathname + location.search);
          navigate(`/login?redirect=${redirect}`, { replace: true });
        }
        setChecking(false);
      });
    } else {
      setChecking(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (checking) {
    // Brief loading state while attempting silent refresh
    return null;
  }

  return isAuthenticated ? (
    <PendingInvitationsProvider>
      <Authenticated>
        <Outlet />
      </Authenticated>
    </PendingInvitationsProvider>
  ) : (
    <Navigate
      to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}
      replace
    />
  );
};

interface AuthenticatedProps {
  children: ReactNode;
}

export const Authenticated: React.FC<AuthenticatedProps> = ({ children }: AuthenticatedProps) => {
  const { count: pendingInviteCount } = usePendingInvitationsContext();
  const [navOpen, setNavOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const auth = useAuth();

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const closeDropdown = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener("click", closeDropdown);
    } else {
      document.removeEventListener("click", closeDropdown);
    }

    return () => {
      document.removeEventListener("click", closeDropdown);
    };
  }, [isDropdownOpen]);

  // Escape closes the user menu (restoring focus to its trigger) or the mobile nav drawer.
  useEffect(() => {
    if (!isDropdownOpen && !navOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (isDropdownOpen) {
        setIsDropdownOpen(false);
        menuButtonRef.current?.focus();
      }
      if (navOpen) setNavOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isDropdownOpen, navOpen]);

  return (
    <div className="flex flex-col h-screen bg-zinc-950 text-white">
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-3 bg-zinc-900 border-b border-zinc-800">
        <div className="flex items-center gap-4">
          <NavLink
            id="tour-logo"
            to="/protected/home"
            className="flex items-center gap-2 select-none hover:opacity-80 transition-opacity"
          >
            <img src="/logo192.png" alt="RoleWeave" className="w-7 h-7 object-contain" />
            <span className="text-lg font-light tracking-widest">
              Role<span className="text-amber-400 font-normal">Weave</span>
            </span>
          </NavLink>
          <button
            className="text-xl text-zinc-400 hover:text-amber-400 transition-colors px-2 md:hidden"
            onClick={() => setNavOpen((o) => !o)}
            aria-label="Toggle navigation"
            aria-controls="app-sidebar"
            aria-expanded={navOpen}
          >
            ☰
          </button>
        </div>
        <nav className="flex items-center space-x-6">
          <NavLink
            to="/protected/marketplace"
            className={({ isActive }) =>
              isActive
                ? "text-amber-400 text-sm tracking-wide"
                : "text-zinc-400 hover:text-white text-sm tracking-wide transition-colors"
            }
          >
            Marketplace
          </NavLink>
          <div className="relative" ref={dropdownRef}>
            <button
              id="tour-user-menu"
              ref={menuButtonRef}
              onClick={toggleDropdown}
              aria-label="User menu"
              aria-haspopup="menu"
              aria-expanded={isDropdownOpen}
              className="w-8 h-8 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-amber-400 text-xs font-semibold hover:border-amber-400 transition-colors"
            >
              {String(auth.get("username") || "U")
                .charAt(0)
                .toUpperCase()}
            </button>
            {isDropdownOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-48 bg-zinc-900 border border-zinc-800 shadow-xl z-50"
              >
                <a
                  role="menuitem"
                  href="/protected/user/profile"
                  className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors"
                >
                  Profile
                </a>
                <button
                  role="menuitem"
                  onClick={auth.logout}
                  className="block w-full px-4 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors border-t border-zinc-800"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </nav>
      </header>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — inline column on desktop, off-canvas drawer on mobile */}
        <aside
          id="app-sidebar"
          className={`bg-zinc-900 border-r border-zinc-800 w-64 md:w-40 flex-shrink-0 fixed md:static inset-y-0 left-0 z-40 md:z-auto transform transition-transform duration-200 md:translate-x-0 pt-16 md:pt-0 ${
            navOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <ul className="space-y-1 p-3 pt-4" onClick={() => setNavOpen(false)}>
            <li>
              <NavLink
                id="tour-nav-home"
                to="/protected/home"
                className={({ isActive }) =>
                  isActive
                    ? "block px-3 py-2 text-sm text-amber-400 bg-amber-400/10 border-l-2 border-amber-400 tracking-wide"
                    : "block px-3 py-2 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800 tracking-wide transition-colors"
                }
              >
                Hearth
              </NavLink>
            </li>
            <li>
              <NavLink
                id="tour-nav-assets"
                to="/protected/assets"
                className={({ isActive }) =>
                  isActive
                    ? "block px-3 py-2 text-sm text-amber-400 bg-amber-400/10 border-l-2 border-amber-400 tracking-wide"
                    : "block px-3 py-2 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800 tracking-wide transition-colors"
                }
              >
                Compendium
              </NavLink>
            </li>
            <li>
              <NavLink
                id="tour-nav-campaigns"
                to="/protected/campaigns"
                className={({ isActive }) =>
                  (isActive
                    ? "block px-3 py-2 text-sm text-amber-400 bg-amber-400/10 border-l-2 border-amber-400 tracking-wide"
                    : "block px-3 py-2 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800 tracking-wide transition-colors") +
                  " flex items-center justify-between"
                }
              >
                <span>Chronicles</span>
                {pendingInviteCount > 0 && (
                  <span className="bg-amber-400 text-zinc-900 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {pendingInviteCount}
                  </span>
                )}
              </NavLink>
            </li>
            <li>
              <NavLink
                id="tour-nav-characters"
                to="/protected/characters"
                className={({ isActive }) =>
                  isActive
                    ? "block px-3 py-2 text-sm text-amber-400 bg-amber-400/10 border-l-2 border-amber-400 tracking-wide"
                    : "block px-3 py-2 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800 tracking-wide transition-colors"
                }
              >
                Heroes
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/protected/fate-formulas"
                className={({ isActive }) =>
                  isActive
                    ? "block px-3 py-2 text-sm text-amber-400 bg-amber-400/10 border-l-2 border-amber-400 tracking-wide"
                    : "block px-3 py-2 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800 tracking-wide transition-colors"
                }
              >
                Formulas
              </NavLink>
            </li>
          </ul>
        </aside>

        {navOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/50 md:hidden"
            onClick={() => setNavOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Main Content */}
        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 overflow-y-auto bg-zinc-950 scrollbar-modern"
        >
          {children}
        </main>
      </div>

      {/* Footer */}
      <footer className="flex items-center justify-between px-6 py-3 bg-zinc-900 border-t border-zinc-800 flex-shrink-0">
        <div className="flex items-center gap-2 select-none">
          <img src="/logo192.png" alt="RoleWeave" className="w-4 h-4 object-contain opacity-60" />
          <span className="text-zinc-500 text-xs tracking-widest">
            Role<span className="text-amber-400/70">Weave</span>
          </span>
        </div>
        <p className="text-zinc-600 text-xs">© 2026 RoleWeave. All rights reserved.</p>
        <div className="flex gap-4">
          <a
            href="/protected/user/profile"
            className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors"
          >
            Weaver Profile
          </a>
          <button
            onClick={auth.logout}
            className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors"
          >
            Sign out
          </button>
        </div>
      </footer>
    </div>
  );
};

export default ProtectedRoute;
